import { Menu } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import * as api from "api/Knowledge";
import { FeedClassKey, TimelineClassKey } from "assets/jss/common-styles";
import AdvFilter, { AdvFilterCommand, AdvFilterComponent, AdvFilterPicker, AdvFilterResult } from "components/AdvFilter";
import { CmdAuthor, CmdPublishEndDate, CmdPublishStartDate, CmdWithVideoOnly, CommandLabels } from "components/AdvFilter/CommonCommands";
import ButtonOrderBy from "components/Buttons/ButtonOrderBy";
import ButtonResetFilter from "components/Buttons/ButtonResetFilter";
import IconButtonCancel from "components/Buttons/IconButtonCancel";
import { SpinnerIndicator } from "components/CallServer";
import FilterCountInfo from "components/FilterCountInfo";
import { MuiFilterAdvancedContainer, MuiFilterAppBar, MuiFilterToolbar } from "components/MuiExtras/Filter";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiMenuItemChecked from "components/MuiExtras/MuiMenuItemChecked";
import MuiPushRight from "components/MuiExtras/MuiPushRight";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import LibUtils, { nprop } from "lib/LibUtils";
import { FeedType, PublicationType } from "models/ApiEnums";
import { FeedAnswersFilter, FeedAnswersOrderBy, FeedContentUpdate } from "models/ApiModels";
import queryString from 'query-string';
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import Session from "services/Session";
import * as FeedActions from 'store/feed/actions';
import { FeedState } from "store/feed/reducer";
import { FeedMessageTypes } from "store/feed/windowMessages";
import { ApplicationState } from "store/rootReducer";
import * as SideBarActions from 'store/sidebar/actions';
import CardManager from "views/Timeline/Components/CardManager";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IStateProps {
    feed: FeedState;
}

interface IState {
    advFilters: AdvFilterResult[];
    orderByMenuAnchorEl: any;
    dialog: any;
    checkingText: boolean;
}

interface IProps {
    questionId: string;
    goAnswerId?: string;
    canAdd: boolean;
    onSidebarResetRequest: () => void;
    onRef?: (ref: FeedAnswersComponent) => void;
    totalAnswers: number;
}

interface IDispatchProps {
    onAddPublication: (publicationId: string) => Promise<void>;
    onReloadPublication: (uid: string, publicationId: string) => Promise<void>;
    onRemovePublication: (uid: string, publicationId: string, publicationType: PublicationType) => Promise<boolean>;
    onUpdatePublication: (newItemDto: FeedContentUpdate) => void;
    onExpandPublication: (uid: string, publicationId: string) => Promise<void>;
    onRemoveFromFeed: (uid: string, publicationId: string) => void;

    onFeedReset: () => void;
    onGetFeed: (filter: FeedAnswersFilter, orderBy: FeedAnswersOrderBy, startIndex: number, endIndex: number) => Promise<any>;
    onSidebarResetRequest: () => void;
    executeFeedMessage: (messageType: FeedMessageTypes) => void;
}

type Props = IProps & IStateProps & IDispatchProps & WithTranslation & StyledComponentProps<TimelineClassKey | FeedClassKey> & WithSnackbar;

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export class FeedAnswersComponent extends React.Component<Props, IState> {

    private filter: FeedAnswersFilter;
    private orderBy: FeedAnswersOrderBy = null;
    private advFilterRef: AdvFilterComponent = null;
    private filterFromQs: boolean = false;

    constructor(props: Props) {
        super(props);

        const q = queryString.parse(document.location.search);

        // o parâmetro "fn" é colocado na query string por 'NotificationMessage'.
        this.orderBy = (window.opener == null || q["fn"] != null) ? FeedAnswersOrderBy.Recent : FeedAnswersOrderBy.Best;

        this.state = {
            advFilters: [],
            orderByMenuAnchorEl: null,
            dialog: null,
            checkingText: props.goAnswerId == null && q["qs"] != null
        };

        this.resetFilter(props);

        const afu = q.afu as string;   // querystring: Answers From User
        if (afu) this.filter.answersFromUser = afu;

        if (this.state.checkingText) {
            // Está em uma busca global, então deve-se exibir todas as respostas com os termos da busca e não somente a resposta indicada.
            this.filter.searchText = q["qs"] as string;
            this.filterFromQs = true;
        }
        else if (props.goAnswerId != null) {
            // Filtra pela resposta solicitada.
            this.filter.answerId = props.goAnswerId;
        }

        window.addEventListener("message", this.receiveMessage, false);
    }

    ///
    private get commands(): AdvFilterCommand[] {
        const cmdLabels = CommandLabels();
        return [
            { keyword: CmdAuthor, displayName: cmdLabels[CmdAuthor], picker: AdvFilterPicker.User },
            { keyword: CmdPublishStartDate, displayName: cmdLabels[CmdPublishStartDate], picker: AdvFilterPicker.Calendar },
            { keyword: CmdPublishEndDate, displayName: cmdLabels[CmdPublishEndDate], picker: AdvFilterPicker.Calendar },
        ];
    };

    ///
    private receiveMessage = (event: MessageEvent) => {
        if (!event.isTrusted) return;
        this.props.executeFeedMessage((event.data as FeedMessageTypes));
    };

    ///
    public feedReset() {
        this.props.onFeedReset();
    }

    ///
    public addItemToFeed(publicationId: string): Promise<void> {
        return this.props.onAddPublication(publicationId);
    };

    //#region FILTER

    ///
    private resetFilter(props: Props) {
        this.filter = {
            questionId: props.questionId,
            answersFromUser: null,
            answerId: null,
            searchText: null,
            startDate: null,
            endDate: null,
            authorId: null,
            videoOnly: null,
        };

        this.filterFromQs = false;
    }

    ///
    private handleFilterUpdate = (searchText: string, advFilters: AdvFilterResult[]) => {
        if (searchText.length == 0 && advFilters.length == 0) {
            this.resetFilter(this.props);
        }
        else {
            this.filter.searchText = searchText.length == 0 ? null : searchText;
            this.filter.authorId = nprop(nprop(advFilters.find(x => x.keyword == CmdAuthor)).value, null);
            this.filter.videoOnly = nprop(nprop(advFilters.find(x => x.keyword == CmdWithVideoOnly) != null), null);

            var dt = advFilters.find(x => x.keyword == CmdPublishStartDate);
            this.filter.startDate = dt ? (dt.value as Date).putStartOfDay() : null;

            dt = advFilters.find(x => x.keyword == CmdPublishEndDate);
            this.filter.endDate = dt ? (dt.value as Date).putEndOfDay() : null;
        }
        this.setState({ advFilters }, () => this.feedReset());
    };

    ///
    private isFiltered() {
        return !LibUtils.isAllPropertiesWithValue(this.filter, null, ["questionId", "answersFromCurrentUser", "searchText"])
            || (this.filter.searchText != null && this.filterFromQs && this.props.totalAnswers > 1)
            || (this.filter.searchText != null && !this.filterFromQs);
    }

    ///
    private applyOrder = (orderBy: FeedAnswersOrderBy) => {
        this.setState({
            orderByMenuAnchorEl: null
        }, () => {
            this.orderBy = orderBy;
            this.feedReset();
        });
    };

    ///
    private renderAdvFilter(): JSX.Element {
        return (
            <MuiFilterAppBar position="static">
                <MuiFilterToolbar variant="dense" >

                    <MuiFilterAdvancedContainer>
                        <AdvFilter
                            id="advfilter"
                            innerRef={(ref) => { this.advFilterRef = ref; }}
                            commands={this.commands}
                            inputValue={this.filter.searchText}
                            selectedCommands={this.state.advFilters}
                            placeHolder={this.props.t("Common.Filter.Adv.InputPlaceholder")}
                            placeHolderFocus={this.props.t("Common.Filter.Adv.InputPlaceholderFocus")}
                            title={this.props.t("Common.Filter.Adv.InputTitleEnterToFilter")}
                            onUpdateSelection={this.handleFilterUpdate}
                        />

                        <MuiPushRight>
                            <ButtonOrderBy
                                onClick={
                                    (e) => {
                                        this.setState({ orderByMenuAnchorEl: e.currentTarget });
                                    }
                                }
                            />
                            <Menu
                                anchorEl={this.state.orderByMenuAnchorEl}
                                open={this.state.orderByMenuAnchorEl != null}
                                onClose={
                                    () => {
                                        this.setState({ orderByMenuAnchorEl: null });
                                    }
                                }
                            >
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MostLiked")} value={FeedAnswersOrderBy.Best} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MostRecent")} value={FeedAnswersOrderBy.Recent} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MostViewed")} value={FeedAnswersOrderBy.MostViews} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MostCommented")} value={FeedAnswersOrderBy.MostCommented} currentSelection={this.orderBy} onClick={this.applyOrder} />
                            </Menu>
                        </MuiPushRight>

                    </MuiFilterAdvancedContainer>
                </MuiFilterToolbar>
            </MuiFilterAppBar>
        );
    }

    //#endregion

    private checkTextFromQs() {
        api.hasSearchTextInAnswers(Session.user.id, this.filter.questionId, this.filter.searchText)
            .then((result) => {
                if (!result) {
                    this.filter.searchText = null;
                    this.filterFromQs = false;
                }
                this.setState({ checkingText: false }, () => this.feedReset());
            })
            .catch(() => {
                this.filter.searchText = null; this.filterFromQs = false;
                this.setState({ checkingText: false }, () => this.feedReset());
            });
    }

    ///
    public componentDidMount() {
        if (this.props.onRef) this.props.onRef(this);

        if (this.state.checkingText) {
            this.checkTextFromQs();
        }
        else {
            this.feedReset();
        }
    }

    ///
    public componentWillUnmount() {
        this.feedReset();
        window.removeEventListener("message", this.receiveMessage);
        if (this.props.onRef) this.props.onRef(null);
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        if (prevProps.questionId != this.props.questionId) {
            this.feedReset();
        }
    }

    ///
    public render() {
        const { classes, feed, feed: { lastError, totalRecords, loading }, snackbar } = this.props;

        if (this.state.checkingText) return <SpinnerIndicator />;

        return (
            <div>
                {this.state.dialog}

                {
                    (totalRecords > 0 || this.isFiltered()) &&
                    <MuiContainer margin="normal">
                        {this.renderAdvFilter()}
                    </MuiContainer>
                }

                {
                    totalRecords > 0 &&
                    <>
                        <MuiContainer margin="normal">
                            {
                                !this.isFiltered()
                                    ? <FilterCountInfo
                                        count={totalRecords}
                                        singularText={this.props.t("Common.Feed.Answer.Filter.CountSingular")}
                                        pluralText={this.props.t("Common.Feed.Answer.Filter.CountPlural")}
                                        additionalInfo=""
                                    />
                                    : (
                                        <FilterCountInfo
                                            count={totalRecords}
                                            singularText={this.props.t("Common.Feed.Answer.Filter.FilteredSingular")}
                                            pluralText={this.props.t("Common.Feed.Answer.Filter.FilteredPlural")}
                                        >
                                            <IconButtonCancel title={this.props.t("Common.Filter.ResetText")} onClick={() => this.advFilterRef.resetFilter()} size="small" />
                                        </FilterCountInfo>
                                    )
                            }
                        </MuiContainer>
                    </>
                }


                <CardManager
                    classes={classes}
                    feedType={FeedType.Answers}
                    feed={feed}
                    snackbar={snackbar}

                    onExpandPublication={this.props.onExpandPublication}
                    onRemovePublication={this.props.onRemovePublication}
                    onReloadPublication={this.props.onReloadPublication}
                    onUpdatePublication={this.props.onUpdatePublication}

                    onRemoveFromFeed={this.props.onRemoveFromFeed}
                    onSidebarResetRequest={this.props.onSidebarResetRequest}

                    onGetFeed={(startIndex: number, endIndex: number) => this.props.onGetFeed(this.filter, this.orderBy, startIndex, endIndex)}
                />

                {
                    lastError != null
                        ? <MuiMessageBox variant="error" message={lastError} />
                        : totalRecords == 0 && !loading && !this.isFiltered()
                            ? <MuiMessageBox variant="info" message={this.props.t("Common.Feed.Answer.Filter.EmptyList")}></MuiMessageBox>
                            : totalRecords == 0 && !loading && this.isFiltered()
                                ? (
                                    <MuiMessageBox
                                        variant="warning"
                                        action={<ButtonResetFilter color="warning" onClick={() => this.advFilterRef.resetFilter()} />}
                                        message={this.props.t("Common.Feed.Answer.Filter.FilteredListEmpty")}
                                    />
                                )
                                : null
                }
            </div >
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

function mapStateToProps(state: ApplicationState): IStateProps {
    return {
        feed: state.feedAnswers
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, void, Action<any>>, ownProps: IProps): IDispatchProps {
    const action = new FeedActions.AnswersFeedAction(ownProps.questionId);

    return {
        onAddPublication: (publicationId) => dispatch(action.addPublication(publicationId)),
        onReloadPublication: (uid, publicationId) => dispatch(action.reloadPublication(uid, publicationId)),
        onRemovePublication: (uid, publicationId, publicationType) => dispatch(action.removePublication(uid, publicationId, publicationType)),
        onExpandPublication: (uid, publicationId) => dispatch(action.getPublicationFullContent(uid, publicationId)),
        onRemoveFromFeed: (uid, publicationId) => dispatch(action.removeFeedData(uid, publicationId)),

        onUpdatePublication: (newItemDto) => dispatch(action.updateFeedData(newItemDto)),

        onFeedReset: () => dispatch(action.feedReset()),
        onGetFeed: (filter, orderBy, startIndex, endIndex) => dispatch(action.getFeed(filter, orderBy, startIndex, endIndex)),
        onSidebarResetRequest: () => dispatch(SideBarActions.reset()),

        executeFeedMessage: (messageType) => action.executeFeedMessage(messageType, dispatch)
    };
}

export default connect<IStateProps, IDispatchProps>(mapStateToProps, mapDispatchToProps)(withSnackbar(withTranslation()(FeedAnswersComponent)));